/**
 * Cart validation for item customization — determines if the current
 * selections satisfy all required groups/minimum selections.
 */
import { useMemo } from 'react'
import { isRowVisible } from '@/utils/ruleEngine'
import type { MenuItem } from '@/types/menu'
import type { RuleEvaluationResult } from '@/types/logicRules'

interface SelectedModifier {
  groupId: string
  quantity?: number
}

interface ComboGroupSelection {
  groupId: string
}

/** Count total quantity for a modifier group (sum of quantity fields, defaulting to 1) */
export function countGroupQty(mods: SelectedModifier[], groupId: string) {
  return mods.filter((m) => m.groupId === groupId).reduce((sum, m) => sum + (m.quantity ?? 1), 0)
}

interface UseCartValidationParams {
  item: MenuItem | null
  selectedModifiers: SelectedModifier[]
  comboGroupSelections: ComboGroupSelection[]
  mainMealCustomizations: SelectedModifier[]
  singleItemRuleResult: RuleEvaluationResult
}

export function useCartValidation({
  item,
  selectedModifiers,
  comboGroupSelections,
  mainMealCustomizations,
  singleItemRuleResult,
}: UseCartValidationParams) {
  const canAddToCart = useMemo(() => {
    if (!item) return false

    // For single items: check required modifier groups (only visible ones)
    if (item.itemType === 'single' && item.modifierGroups) {
      const visibleGroups = item.modifierGroups.filter((g) =>
        isRowVisible(g.id, singleItemRuleResult, true)
      )
      const requiredGroups = visibleGroups.filter((g) => g.required)
      for (const group of requiredGroups) {
        const hasSelection = selectedModifiers.some((m) => m.groupId === group.id)
        if (!hasSelection) return false
      }

      // Check min/max selections for multi-select, quantity, AND single groups
      for (const group of visibleGroups) {
        const totalQty = countGroupQty(selectedModifiers, group.id)
        if (group.minSelections && totalQty < group.minSelections) return false
        if (group.maxSelections && totalQty > group.maxSelections) return false
      }
    }

    // For combo items: check minimum selections are met for each group
    if (item.itemType === 'combo' && item.comboProduct?.groups) {
      for (const group of item.comboProduct.groups) {
        if (group.minSelections > 0) {
          const selectionCount = comboGroupSelections.filter((s) => s.groupId === group.id).length
          if (selectionCount < group.minSelections) return false
        }
      }
    }

    // For combo items: check main meal row minSelections
    if (item.itemType === 'combo' && item.comboProduct?.mainMeal?.rows) {
      for (const row of item.comboProduct.mainMeal.rows) {
        if (row.minSelections > 0) {
          const totalQty = countGroupQty(mainMealCustomizations, row.id)
          if (totalQty < row.minSelections) return false
        }
      }
    }

    return true
  }, [item, selectedModifiers, comboGroupSelections, mainMealCustomizations, singleItemRuleResult])

  const remainingRequired = useMemo(() => {
    if (!item) return 0
    let remaining = 0

    if (item.itemType === 'single' && item.modifierGroups) {
      const visibleGroups = item.modifierGroups.filter((g) =>
        isRowVisible(g.id, singleItemRuleResult, true)
      )
      const requiredGroups = visibleGroups.filter((g) => g.required)
      for (const group of requiredGroups) {
        const hasSelection = selectedModifiers.some((m) => m.groupId === group.id)
        if (!hasSelection) remaining += 1
      }
      for (const group of visibleGroups) {
        if ((group.minSelections || 0) > 0) {
          const totalQty = countGroupQty(selectedModifiers, group.id)
          if (totalQty < (group.minSelections || 0)) {
            remaining += (group.minSelections || 0) - totalQty
          }
        }
      }
    }

    if (item.itemType === 'combo' && item.comboProduct?.groups) {
      for (const group of item.comboProduct.groups) {
        if (group.minSelections > 0) {
          const selectionCount = comboGroupSelections.filter((s) => s.groupId === group.id).length
          if (selectionCount < group.minSelections) {
            remaining += group.minSelections - selectionCount
          }
        }
      }
    }

    if (item.itemType === 'combo' && item.comboProduct?.mainMeal?.rows) {
      for (const row of item.comboProduct.mainMeal.rows) {
        if (row.minSelections > 0) {
          const totalQty = countGroupQty(mainMealCustomizations, row.id)
          if (totalQty < row.minSelections) {
            remaining += row.minSelections - totalQty
          }
        }
      }
    }

    return remaining
  }, [item, selectedModifiers, comboGroupSelections, mainMealCustomizations, singleItemRuleResult])

  return { canAddToCart, remainingRequired }
}
